import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useClerk } from "@clerk/react";
import { Clock, XCircle, Building2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/common";
import { useMe } from "@/context/MeContext";
import { API, useApi, errMsg } from "@/lib/api";

const RegisterForm = ({ me }) => {
  const api = useApi();
  const { refresh } = useMe();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ full_name: me.name || "", phone: me.phone || "", project_id: "", unit: me.unit || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/projects`).then((r) => setProjects(r.data));
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) return toast.error("اختر المشروع");
    setBusy(true);
    try {
      await api("post", "/me/register", form);
      toast.success("تم إرسال طلبك للمراجعة");
      await refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="owner-register-form">
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-[#C59B27]" />
        <div>
          <h1 className="text-2xl font-extrabold text-[#0F382C]">ربط حسابك بوحدتك السكنية</h1>
          <p className="text-sm text-[#5C7067]">سيتم تفعيل الخدمات بعد تحقق الإدارة من ملكيتك.</p>
        </div>
      </div>
      {me.status === "rejected" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="rejected-notice">
          تم رفض طلبك السابق{me.reject_reason ? `: ${me.reject_reason}` : ""}. يمكنك تعديل البيانات وإعادة الإرسال.
        </div>
      )}
      <div className="space-y-2"><Label>الاسم الكامل</Label><Input required value={form.full_name} onChange={set("full_name")} data-testid="register-name-input" /></div>
      <div className="space-y-2"><Label>رقم الجوال</Label><Input required dir="ltr" className="text-right" placeholder="05XXXXXXXX" value={form.phone} onChange={set("phone")} data-testid="register-phone-input" /></div>
      <div className="space-y-2">
        <Label>المشروع السكني</Label>
        <Select dir="rtl" value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
          <SelectTrigger data-testid="register-project-select"><SelectValue placeholder="اختر مشروعك" /></SelectTrigger>
          <SelectContent>
            {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`register-project-option-${p.id}`}>{p.name} — {p.city}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label>رقم الوحدة</Label><Input required placeholder="مثال: فيلا 12 / شقة 402" value={form.unit} onChange={set("unit")} data-testid="register-unit-input" /></div>
      <Button type="submit" disabled={busy} data-testid="register-submit-button" className="h-12 w-full rounded-full bg-[#0F382C] text-base hover:bg-[#164E3D]">
        {busy ? "جاري الإرسال..." : "إرسال طلب الاعتماد"}
      </Button>
    </form>
  );
};

const Pending = ({ me }) => (
  <div className="text-center space-y-4" data-testid="pending-approval-card">
    <Clock className="mx-auto h-14 w-14 text-[#D97706]" />
    <h1 className="text-2xl font-extrabold text-[#0F382C]">طلبك قيد المراجعة من قبل إدارة دارنا</h1>
    <p className="text-[#5C7067]">سنفعّل حسابك فور التحقق من ملكيتك للوحدة.</p>
    <div className="rounded-xl bg-[#F7F4EF] p-4 text-sm text-right space-y-1">
      <p><b>المشروع:</b> {me.project?.name}</p>
      <p><b>الوحدة:</b> {me.unit}</p>
      <p><b>الجوال:</b> <span className="ltr-num">{me.phone}</span></p>
    </div>
  </div>
);

const Inactive = () => (
  <div className="text-center space-y-4" data-testid="project-inactive-card">
    <XCircle className="mx-auto h-14 w-14 text-red-600" />
    <h1 className="text-2xl font-extrabold text-[#0F382C]">المشروع غير مفعّل حالياً</h1>
    <p className="text-[#5C7067]">الخدمات متوقفة لمشروعك. يرجى التواصل مع إدارة المشروع.</p>
  </div>
);

export default function Onboarding() {
  const { me, refresh } = useMe();
  const { signOut } = useClerk();
  const view = me.status === "pending" ? <Pending me={me} /> : me.status === "approved" ? <Inactive /> : <RegisterForm me={me} />;
  return (
    <div className="min-h-screen bg-[#FAF7F2] pattern-bg">
      <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-6">
        <Logo />
        <Button variant="ghost" onClick={() => signOut()} data-testid="onboarding-signout-button"><LogOut className="ml-2 h-4 w-4" />خروج</Button>
      </div>
      <div className="mx-auto max-w-xl px-4 pb-16">
        <div className="rounded-[2rem] border border-[#E5DEC9] bg-white p-6 sm:p-10 shadow-sm fade-up">{view}</div>
        {me.status === "pending" && (
          <Button variant="outline" onClick={refresh} className="mt-4 w-full rounded-full" data-testid="refresh-status-button">تحديث الحالة</Button>
        )}
      </div>
    </div>
  );
}
